import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader, useConfirm, EmptyState, LoadingState, RelatedLinks } from '../components/ui';
import { ideaBacklinks } from '../linkedin/ideaLinks';
import { FlowStepBar } from '../linkedin';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { postIdeaApi, type PostIdea } from '../services/api';
import { blobSpeichern } from '../services/download';
import {
  mediaApi,
  groesseLesbar,
  KIND_LABELS,
  PROFILE_LABELS,
  MEDIA_ACCEPT,
  fassungsHinweis,
  type MediaAsset,
  type MediaFilter,
  type MediaKind,
  type MediaProfile,
} from '../services/mediaService';
import { MediaPreview } from '../components/media/MediaPreview';
import { Vorschaubild, vorschauVergessen } from '../components/media/Vorschaubild';
import { useMediaUpload, uploadStatusText } from '../components/media/useMediaUpload';

const KINDS = Object.keys(KIND_LABELS) as MediaKind[];
const PROFILES = Object.keys(PROFILE_LABELS) as MediaProfile[];

interface Entwurf {
  title: string;
  topic: string;
  profile: MediaProfile | '';
  postIdeaId: string;
}

const datum = (iso: string) => new Date(iso).toLocaleDateString('de-DE');

type Ansicht = 'liste' | 'kacheln';
const ANSICHT_SCHLUESSEL = 'intermal.medien.ansicht';

function gespeicherteAnsicht(): Ansicht {
  try {
    return localStorage.getItem(ANSICHT_SCHLUESSEL) === 'kacheln' ? 'kacheln' : 'liste';
  } catch {
    return 'liste';
  }
}

const VORSCHAU_HINWEIS: Record<string, string> = {
  fehler: 'Vorschaubild fehlgeschlagen',
  keine: 'Ohne Vorschaubild (Dokument)',
};

/** Kleiner Hinweis zu den Plattform-Fassungen (Kachel und Zeile). */
const FassungsHinweis: React.FC<{ asset: MediaAsset }> = ({ asset }) => {
  const hinweis = fassungsHinweis(asset);
  if (!hinweis) return null;
  return hinweis === 'fehler' ? (
    <span className="badge badge-error" title="Eine Plattform-Fassung ist fehlgeschlagen – Details in der Vorschau">Fassung fehlgeschlagen</span>
  ) : (
    <span className="badge badge-warning" title="LinkedIn-, Reel-, X- bzw. Instagram-Fassung entsteht im Hintergrund">Fassungen in Arbeit</span>
  );
};

/**
 * Medienbibliothek: Kampagnenvideos, PDF-Karussells, Bilder und
 * Office-Dokumente des ganzen Mandanten. Alle im Mandanten sehen dieselben
 * Medien; Hochladen, Aendern und Loeschen braucht linkedin.bearbeiten.
 */
const MediaLibraryPage: React.FC = () => {
  const { confirm } = useConfirm();
  const darfBearbeiten = useBerechtigung().darf('linkedin.bearbeiten');
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  // ?postIdeaId= (Sprung-Chip) zeigt nur die Medien dieser Idee.
  const [filter, setFilter] = useState<MediaFilter>(() => ({
    topic: '', kind: '', profile: '', q: '', postIdeaId: searchParams.get('postIdeaId') || undefined,
  }));
  const [ideen, setIdeen] = useState<PostIdea[]>([]);
  const [vorschau, setVorschau] = useState<MediaAsset | null>(null);
  const [bearbeiteId, setBearbeiteId] = useState<string | null>(null);
  const [entwurf, setEntwurf] = useState<Entwurf>({ title: '', topic: '', profile: '', postIdeaId: '' });
  const [neuThema, setNeuThema] = useState('');
  const [neuProfil, setNeuProfil] = useState<MediaProfile | ''>('');
  const [ziehen, setZiehen] = useState(false);
  const dateiFeld = useRef<HTMLInputElement>(null);
  const [ansicht, setAnsichtState] = useState<Ansicht>(gespeicherteAnsicht);
  // Zaehler je Medium: hochzaehlen laedt das Vorschaubild neu.
  const [vorschauVersion, setVorschauVersion] = useState<Record<string, number>>({});
  const [vorschauLaeuft, setVorschauLaeuft] = useState<string | null>(null);

  const setAnsicht = (neu: Ansicht) => {
    setAnsichtState(neu);
    try {
      localStorage.setItem(ANSICHT_SCHLUESSEL, neu);
    } catch {
      // ohne Speicher: Ansicht gilt nur bis zum Neuladen
    }
  };

  const laden = useCallback(async (f: MediaFilter) => {
    try {
      setLoading(true);
      setError('');
      setAssets(await mediaApi.list(f));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Die Medien konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter mit kurzer Verzoegerung, damit die Suche nicht je Taste laedt.
  useEffect(() => {
    const t = setTimeout(() => { laden(filter); }, 250);
    return () => clearTimeout(t);
  }, [filter, laden]);

  useEffect(() => {
    if (!darfBearbeiten) return;
    postIdeaApi.getAll().then((liste) => setIdeen(Array.isArray(liste) ? liste : [])).catch(() => setIdeen([]));
  }, [darfBearbeiten]);

  const nachUpload = useCallback(() => { laden(filter); }, [filter, laden]);
  const { zeilen, aktiv, hochladen, leeren } = useMediaUpload(nachUpload);

  const ideenFilterTitel = filter.postIdeaId
    ? assets.find((a) => a.postIdea?.id === filter.postIdeaId)?.postIdea?.title
      ?? ideen.find((i) => i.id === filter.postIdeaId)?.title
    : undefined;

  const ideenFilterAufheben = () => {
    setFilter((f) => ({ ...f, postIdeaId: undefined }));
    const rest = new URLSearchParams(searchParams);
    rest.delete('postIdeaId');
    setSearchParams(rest, { replace: true });
  };

  const themen = useMemo(
    () => Array.from(new Set(assets.map((a) => a.topic).filter((t): t is string => Boolean(t)))).sort(),
    [assets],
  );

  const dateienWaehlen = (liste: FileList | null) => {
    if (!liste || liste.length === 0) return;
    hochladen(Array.from(liste), { topic: neuThema || undefined, profile: neuProfil || undefined });
    if (dateiFeld.current) dateiFeld.current.value = '';
  };

  const bearbeiten = (a: MediaAsset) => {
    setBearbeiteId(a.id);
    setEntwurf({ title: a.title || '', topic: a.topic || '', profile: a.profile || '', postIdeaId: a.postIdeaId || '' });
  };

  const speichern = async (id: string) => {
    try {
      const neu = await mediaApi.update(id, {
        title: entwurf.title.trim() || null,
        topic: entwurf.topic.trim() || null,
        profile: entwurf.profile || null,
        postIdeaId: entwurf.postIdeaId || null,
      });
      setAssets((alt) => alt.map((a) => (a.id === id ? { ...a, ...neu } : a)));
      setBearbeiteId(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Das Medium konnte nicht gespeichert werden.');
    }
  };

  const loeschen = async (a: MediaAsset) => {
    const ok = await confirm({
      title: 'Medium löschen?',
      message: `„${a.title || a.originalName}“ wird für alle im Mandanten gelöscht. Das lässt sich nicht rückgängig machen.`,
      confirmLabel: 'Löschen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await mediaApi.remove(a.id);
      setAssets((alt) => alt.filter((x) => x.id !== a.id));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Das Medium konnte nicht gelöscht werden.');
    }
  };

  const vorschauNeu = async (a: MediaAsset) => {
    setVorschauLaeuft(a.id);
    try {
      const ergebnis = await mediaApi.vorschauNeu(a.id);
      vorschauVergessen(a.id);
      setAssets((alt) => alt.map((x) => (x.id === a.id
        ? { ...x, vorschauStatus: ergebnis.vorschauStatus ?? x.vorschauStatus, vorschauFehler: ergebnis.vorschauFehler }
        : x)));
      setVorschauVersion((v) => ({ ...v, [a.id]: (v[a.id] ?? 0) + 1 }));
      if (ergebnis.laeuft) setError('Das Vorschaubild wird im Hintergrund erzeugt. Bitte die Liste gleich neu laden.');
      else if (ergebnis.vorschauStatus === 'fehler') setError(`Vorschaubild fehlgeschlagen: ${ergebnis.vorschauFehler || 'unbekannter Fehler'}`);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Das Vorschaubild konnte nicht erzeugt werden.');
    } finally {
      setVorschauLaeuft(null);
    }
  };

  const vorschaubild = (a: MediaAsset, groesse: 'm' | 'l') => (
    <Vorschaubild
      assetId={a.id}
      kind={a.kind}
      vorschau={a.vorschauStatus === 'ok'}
      groesse={groesse}
      version={vorschauVersion[a.id] ?? 0}
      alt={`Vorschau: ${a.title || a.originalName}`}
    />
  );

  const herunterladen = async (a: MediaAsset) => {
    try {
      blobSpeichern(await mediaApi.content(a.id, true), a.originalName);
    } catch {
      setError('Der Download ist fehlgeschlagen.');
    }
  };

  return (
    <div className="page-container">
      <FlowStepBar phaseId="posts" />
      <PageHeader
        eyebrow="LinkedIn-Studio · Beiträge"
        title="Medienbibliothek"
        subtitle="Videos, PDF-Karussells, Bilder und Dokumente für Kampagnen. Alle im Mandanten sehen und pflegen dieselben Medien."
      />

      {error && <div className="alert alert-error">{error}</div>}

      {darfBearbeiten && (
        <section className="card mb-4" aria-label="Hochladen">
          <div
            role="button"
            tabIndex={0}
            aria-label="Dateien hierher ziehen oder auswählen"
            onClick={() => dateiFeld.current?.click()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') dateiFeld.current?.click(); }}
            onDragOver={(e) => { e.preventDefault(); setZiehen(true); }}
            onDragLeave={() => setZiehen(false)}
            onDrop={(e) => { e.preventDefault(); setZiehen(false); dateienWaehlen(e.dataTransfer.files); }}
            style={{
              border: `2px dashed ${ziehen ? 'var(--color-primary, #c0392b)' : 'var(--color-border, #ccc)'}`,
              borderRadius: 8, padding: '24px 16px', textAlign: 'center', cursor: 'pointer',
            }}
          >
            <strong>Dateien hierher ziehen</strong> oder klicken zum Auswählen
            <div className="text-sm text-muted mt-1">MP4/MOV, PDF, PNG/JPEG/WebP, DOCX/XLSX/ODT · bis 500 MB je Datei</div>
          </div>
          <input
            ref={dateiFeld}
            type="file"
            multiple
            accept={MEDIA_ACCEPT}
            data-testid="media-datei"
            style={{ display: 'none' }}
            onChange={(e) => dateienWaehlen(e.target.files)}
          />
          <div className="flex gap-3 mt-3" style={{ flexWrap: 'wrap' }}>
            <label className="form-label" style={{ flex: '1 1 200px' }}>
              Thema für neue Dateien
              <input className="form-input" value={neuThema} onChange={(e) => setNeuThema(e.target.value)} list="media-themen" placeholder="z. B. NIS2" />
            </label>
            <label className="form-label" style={{ flex: '1 1 160px' }}>
              Profil für neue Dateien
              <select className="form-input" value={neuProfil} onChange={(e) => setNeuProfil(e.target.value as MediaProfile | '')}>
                <option value="">–</option>
                {PROFILES.map((p) => <option key={p} value={p}>{PROFILE_LABELS[p]}</option>)}
              </select>
            </label>
          </div>
          {zeilen.length > 0 && (
            <ul className="mt-3" aria-label="Uploads" style={{ listStyle: 'none', padding: 0 }}>
              {zeilen.map((z) => (
                <li key={z.key} className="text-sm" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ flex: '0 1 280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{z.name}</span>
                  <progress max={1} value={z.anteil} style={{ flex: '1 1 auto' }} aria-label={`Fortschritt ${z.name}`} />
                  <span className={z.status === 'fehler' ? 'text-danger' : 'text-muted'}>{uploadStatusText(z)}</span>
                </li>
              ))}
              {!aktiv && (
                <li><button type="button" className="btn btn-secondary btn-sm" onClick={leeren}>Liste leeren</button></li>
              )}
            </ul>
          )}
        </section>
      )}

      {filter.postIdeaId && (
        <div className="info-panel mb-3" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <span>Nur Medien zur Idee <strong>{ideenFilterTitel ?? 'aus dem Link'}</strong></span>
          <RelatedLinks items={ideaBacklinks(filter.postIdeaId, { omit: ['media'] })} label="Verknüpft mit dieser Idee" />
          <button type="button" className="btn btn-secondary btn-sm" onClick={ideenFilterAufheben} style={{ marginLeft: 'auto' }}>Alle Medien zeigen</button>
        </div>
      )}

      <section className="flex gap-3 mb-3" aria-label="Filter" style={{ flexWrap: 'wrap' }}>
        <label className="form-label" style={{ flex: '2 1 220px' }}>
          Suche
          <input
            className="form-input"
            type="search"
            value={filter.q}
            onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
            placeholder="Titel, Dateiname, Thema"
          />
        </label>
        <label className="form-label" style={{ flex: '1 1 160px' }}>
          Thema
          <input className="form-input" value={filter.topic} list="media-themen" onChange={(e) => setFilter((f) => ({ ...f, topic: e.target.value }))} />
        </label>
        <label className="form-label" style={{ flex: '1 1 140px' }}>
          Typ
          <select className="form-input" value={filter.kind} onChange={(e) => setFilter((f) => ({ ...f, kind: e.target.value as MediaKind | '' }))}>
            <option value="">Alle</option>
            {KINDS.map((k) => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
          </select>
        </label>
        <label className="form-label" style={{ flex: '1 1 140px' }}>
          Profil
          <select className="form-input" value={filter.profile} onChange={(e) => setFilter((f) => ({ ...f, profile: e.target.value as MediaProfile | '' }))}>
            <option value="">Alle</option>
            {PROFILES.map((p) => <option key={p} value={p}>{PROFILE_LABELS[p]}</option>)}
          </select>
        </label>
        <datalist id="media-themen">
          {themen.map((t) => <option key={t} value={t} />)}
        </datalist>
        <div className="btn-group" role="group" aria-label="Ansicht" style={{ alignSelf: 'flex-end' }}>
          <button
            type="button"
            className={`btn btn-sm ${ansicht === 'liste' ? 'btn-primary' : 'btn-secondary'}`}
            aria-pressed={ansicht === 'liste'}
            onClick={() => setAnsicht('liste')}
          >
            Liste
          </button>
          <button
            type="button"
            className={`btn btn-sm ${ansicht === 'kacheln' ? 'btn-primary' : 'btn-secondary'}`}
            aria-pressed={ansicht === 'kacheln'}
            onClick={() => setAnsicht('kacheln')}
          >
            Kacheln
          </button>
        </div>
      </section>

      {loading && assets.length === 0 ? (
        <LoadingState />
      ) : assets.length === 0 ? (
        <EmptyState
          icon="image"
          title="Noch keine Medien"
          description={darfBearbeiten
            ? 'Videos, PDFs und Bilder hierher ziehen. Sie stehen danach allen im Mandanten zur Verfügung.'
            : 'Sobald jemand im Mandanten Medien hochlädt, stehen sie hier.'}
        />
      ) : ansicht === 'kacheln' ? (
        <ul className="medien-kacheln" aria-label="Medien" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {assets.map((a) => (
            <li key={a.id} className="medien-kachel">
              <button
                type="button"
                className="medien-kachel__bild"
                onClick={() => setVorschau(a)}
                disabled={a.kind === 'document'}
                title={a.kind === 'document' ? 'Für Office-Dokumente gibt es keine Vorschau' : 'Vorschau öffnen'}
              >
                {vorschaubild(a, 'l')}
              </button>
              <p className="medien-kachel__titel" title={a.title || a.originalName}>{a.title || a.originalName}</p>
              <div className="medien-kachel__meta">
                {KIND_LABELS[a.kind] ?? a.kind} · {groesseLesbar(a.sizeBytes)}{a.topic ? ` · ${a.topic}` : ''}
              </div>
              {a.postIdea && (
                <div className="medien-kachel__meta">
                  <Link to={`/post-ideas?id=${encodeURIComponent(a.postIdea.id)}`} title="Beitragsidee öffnen">{a.postIdea.title}</Link>
                </div>
              )}
              {a.vorschauStatus && VORSCHAU_HINWEIS[a.vorschauStatus] && a.kind !== 'document' && (
                <div className="medien-kachel__meta text-danger" title={a.vorschauFehler || undefined}>{VORSCHAU_HINWEIS[a.vorschauStatus]}</div>
              )}
              {fassungsHinweis(a) && (
                <div className="medien-kachel__meta"><FassungsHinweis asset={a} /></div>
              )}
              <div className="medien-kachel__aktionen">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => herunterladen(a)}>Herunterladen</button>
                {darfBearbeiten && a.kind !== 'document' && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => vorschauNeu(a)}
                    disabled={vorschauLaeuft === a.id}
                    title="Vorschaubild aus der Datei neu erzeugen"
                  >
                    {vorschauLaeuft === a.id ? 'Wird erzeugt …' : 'Vorschaubild neu'}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Titel / Datei</th>
                <th>Typ</th>
                <th>Thema</th>
                <th>Profil</th>
                <th>Beitragsidee</th>
                <th>Größe</th>
                <th>Hochgeladen</th>
                <th className="text-center">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {assets.map((a) => (bearbeiteId === a.id ? (
                <tr key={a.id}>
                  <td>
                    <input className="form-input" aria-label="Titel" value={entwurf.title} onChange={(e) => setEntwurf({ ...entwurf, title: e.target.value })} placeholder={a.originalName} />
                  </td>
                  <td>{KIND_LABELS[a.kind] ?? a.kind}</td>
                  <td>
                    <input className="form-input" aria-label="Thema" value={entwurf.topic} list="media-themen" onChange={(e) => setEntwurf({ ...entwurf, topic: e.target.value })} />
                  </td>
                  <td>
                    <select className="form-input" aria-label="Profil" value={entwurf.profile} onChange={(e) => setEntwurf({ ...entwurf, profile: e.target.value as MediaProfile | '' })}>
                      <option value="">–</option>
                      {PROFILES.map((p) => <option key={p} value={p}>{PROFILE_LABELS[p]}</option>)}
                    </select>
                  </td>
                  <td>
                    <select className="form-input" aria-label="Beitragsidee" value={entwurf.postIdeaId} onChange={(e) => setEntwurf({ ...entwurf, postIdeaId: e.target.value })}>
                      <option value="">– keine –</option>
                      {a.postIdea && !ideen.some((i) => i.id === a.postIdea!.id) && (
                        <option value={a.postIdea.id}>{a.postIdea.title}</option>
                      )}
                      {ideen.map((i) => <option key={i.id} value={i.id}>{i.title}</option>)}
                    </select>
                  </td>
                  <td>{groesseLesbar(a.sizeBytes)}</td>
                  <td>{datum(a.createdAt)}</td>
                  <td className="text-center">
                    <div className="btn-group justify-center">
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => speichern(a.id)}>Speichern</button>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setBearbeiteId(null)}>Abbrechen</button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={a.id}>
                  <td>
                    <div className="mit-vorschau">
                      {vorschaubild(a, 'm')}
                      <div className="mit-vorschau__text">
                        <strong>{a.title || a.originalName}</strong>
                        {fassungsHinweis(a) && <> <FassungsHinweis asset={a} /></>}
                        {a.title && <div className="text-sm text-muted">{a.originalName}</div>}
                        {a.vorschauStatus === 'fehler' && (
                          <div className="text-xs text-muted" title={a.vorschauFehler || undefined}>
                            Vorschaubild fehlgeschlagen
                            {darfBearbeiten && (
                              <>
                                {' · '}
                                <button
                                  type="button"
                                  className="btn btn-ghost btn-sm"
                                  style={{ padding: '0 4px' }}
                                  onClick={() => vorschauNeu(a)}
                                  disabled={vorschauLaeuft === a.id}
                                >
                                  {vorschauLaeuft === a.id ? 'wird erzeugt …' : 'neu erzeugen'}
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{KIND_LABELS[a.kind] ?? a.kind}</td>
                  <td>{a.topic || <span className="text-muted">–</span>}</td>
                  <td>{a.profile ? PROFILE_LABELS[a.profile] : <span className="text-muted">–</span>}</td>
                  <td>
                    {a.postIdea
                      ? <Link to={`/post-ideas?id=${encodeURIComponent(a.postIdea.id)}`} title="Beitragsidee öffnen">{a.postIdea.title}</Link>
                      : <span className="text-muted">–</span>}
                  </td>
                  <td>{groesseLesbar(a.sizeBytes)}</td>
                  <td>{datum(a.createdAt)}</td>
                  <td className="text-center">
                    <div className="btn-group justify-center">
                      {a.kind !== 'document' && (
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setVorschau(a)}>Vorschau</button>
                      )}
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => herunterladen(a)}>Herunterladen</button>
                      {darfBearbeiten && (
                        <>
                          <button type="button" className="btn btn-primary btn-sm" onClick={() => bearbeiten(a)}>Bearbeiten</button>
                          <button type="button" className="btn btn-danger btn-sm" onClick={() => loeschen(a)}>Löschen</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      )}

      {vorschau && <MediaPreview asset={vorschau} onClose={() => setVorschau(null)} darfBearbeiten={darfBearbeiten} />}
    </div>
  );
};

export default MediaLibraryPage;
